import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Download } from "lucide-react";
import { DataTable } from "@/components/ui/data-table";
import { Checkbox } from "@/components/ui/checkbox";
import { DocumentPermissionsDialog } from "./DocumentPermissionsDialog";
import { DeleteDocumentDialog } from "./DeleteDocumentDialog";
import { DocumentDetailDialog } from "./DocumentDetailDialog";
import {
  formatFileSize,
  resolveMediaUrl,
} from "@/features/documents/lib/resolve-media-url";
import type { Document } from "@/types/document";
import { formatOptionalArabicDate } from "@/lib/formatters";

function buildColumns(
  departmentLookup: Record<string, string>,
  libraryLookup: Record<string, string>,
  canManage: boolean,
  selected: Set<string>,
  allOnPageSelected: boolean,
  onToggle: (id: string) => void,
  onToggleAll: () => void,
): ColumnDef<Document>[] {
  return [
    {
      id: "select",
      header: () => (
        <Checkbox
          checked={allOnPageSelected}
          onCheckedChange={onToggleAll}
          aria-label="تحديد الكل"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selected.has(row.original.id)}
          onCheckedChange={() => onToggle(row.original.id)}
          aria-label="تحديد الصف"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    { accessorKey: "name", header: "الاسم" },
    {
      accessorKey: "language",
      header: "اللغة",
      cell: ({ row }) => {
        // 🔴 AN UNLABELLED DOCUMENT IS NOT ARABIC. NULL means it is served on BOTH locales, so
        // showing "العربية" here misreports the 619 migrated documents and invites an editor to
        // "correct" it into an Arabic-only stamp that drops it from /en.
        const lang = row.original.language;
        if (lang === "en") return "English only";
        if (lang === "ar") return "العربية فقط";
        return "اللغتان";
      },
    },
    {
      accessorKey: "mimeType",
      header: "النوع",
      cell: ({ row }) => row.original.mimeType || "—",
    },
    {
      accessorKey: "sizeBytes",
      header: "الحجم",
      cell: ({ row }) => formatFileSize(Number(row.original.sizeBytes)),
    },
    {
      accessorKey: "departmentId",
      header: "الدائرة",
      cell: ({ row }) => {
        const id = row.original.departmentId;
        return id ? (departmentLookup[id] ?? id) : "—";
      },
    },
    {
      accessorKey: "libraryId",
      header: "المكتبة",
      cell: ({ row }) => {
        const id = row.original.libraryId;
        return id ? (libraryLookup[id] ?? id) : "—";
      },
    },
    {
      accessorKey: "createdAt",
      header: "تاريخ الرفع",
      cell: ({ row }) =>
        formatOptionalArabicDate(row.original.createdAt),
    },
    {
      id: "actions",
      header: "الإجراءات",
      enableHiding: false,
      cell: ({ row }) => {
        const href = resolveMediaUrl(row.original.url);
        return (
          <div className="flex items-center justify-center gap-3">
            <DocumentDetailDialog
              document={row.original}
              departmentName={
                row.original.departmentId
                  ? departmentLookup[row.original.departmentId]
                  : undefined
              }
              libraryName={
                row.original.libraryId
                  ? libraryLookup[row.original.libraryId]
                  : undefined
              }
            />
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                aria-label="تنزيل المستند"
                title="تنزيل"
              >
                <Download className="w-4 h-4" />
              </a>
            ) : (
              <span className="text-muted-foreground/40" title="لا يوجد ملف">
                <Download className="w-4 h-4" />
              </span>
            )}
            {canManage && (
              <>
                <DocumentPermissionsDialog document={row.original} />
                <DeleteDocumentDialog document={row.original} />
              </>
            )}
          </div>
        );
      },
    },
  ];
}

interface DocumentsTableProps {
  documents: Document[];
  departmentLookup: Record<string, string>;
  libraryLookup: Record<string, string>;
  canManage: boolean;
  /** 1-based current page. */
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  selected: Set<string>;
  allOnPageSelected: boolean;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
}

export function DocumentsTable({
  documents,
  departmentLookup,
  libraryLookup,
  canManage,
  page,
  pageCount,
  onPageChange,
  selected,
  allOnPageSelected,
  onToggle,
  onToggleAll,
}: DocumentsTableProps) {
  const columns = useMemo(
    () =>
      buildColumns(
        departmentLookup,
        libraryLookup,
        canManage,
        selected,
        allOnPageSelected,
        onToggle,
        onToggleAll,
      ),
    [
      departmentLookup,
      libraryLookup,
      canManage,
      selected,
      allOnPageSelected,
      onToggle,
      onToggleAll,
    ],
  );

  return (
    <DataTable
      columns={columns}
      data={documents}
      manualPagination={{
        pageIndex: page - 1,
        pageCount,
        onPageChange: (index) => onPageChange(index + 1),
      }}
    />
  );
}
